<script setup lang="ts">
	import { useAiReportStore } from '@/stores/aiReport'
	import {
		BarElement,
		CategoryScale,
		Chart as ChartJS,
		Legend,
		LineElement,
		LinearScale,
		PointElement,
		Tooltip,
	} from 'chart.js'
	import html2canvas from 'html2canvas'
	import jsPDF from 'jspdf'
	import { marked } from 'marked'
	import moment from 'moment'
	import 'moment/dist/locale/es'
	import { computed, ref, watch } from 'vue'
	import { Bar, Line } from 'vue-chartjs'
	import { useRoute } from 'vue-router'

	import ModalComponent from '@/components/ModalComponent.vue'

	import { useAuth } from '@/composables/useAuth'
	import useHasPermissionUser from '@/composables/useHasPermissionUser'
	import { CONTACTS } from '@/config/contacts'

	import useCompany from '@/modules/companies/composables/useCompany'
	import PlanGateModal from '@/modules/plans/components/PlanGateModal.vue'
	import usePlanFeature from '@/modules/plans/composables/usePlanFeature'
	import useAiReportUsage, {
		type AiReportPackageOption,
	} from '@/modules/reports/composables/useAiReportUsage'
	import useAiTravelReport, {
		getAiReport,
	} from '@/modules/reports/composables/useAiTravelReport'
	import type {
		AiChart,
		AiReportFocus,
		AiReportPeriodPreset,
		AiReportSeverity,
		AiTravelReport,
	} from '@/modules/reports/interfaces/aiTravelReport'

	ChartJS.register(
		CategoryScale,
		LinearScale,
		PointElement,
		LineElement,
		BarElement,
		Tooltip,
		Legend
	)

	moment.locale('es')

	// Reusable: además de página (/reports/ai-travel), se monta como tab del perfil
	// de empresa pasando companyId por prop (AiReportTab).
	const props = defineProps<{ companyId?: number }>()

	const { user } = useAuth()

	// ─── Multi-empresa ───────────────────────────────────────────────────────────
	// Prioridad de empresa: prop (tab del perfil) > ?company={id} (deep link de
	// notificaciones) > la del usuario. El backend autoriza view-ai-reports por
	// empresa en cada request.

	const selectedCompanyId = ref<number | null>(null)

	const companyId = computed(
		() =>
			props.companyId ?? selectedCompanyId.value ?? user.value?.companyId ?? 0
	)

	// Montada como tab del perfil: sin título propio ni padding de página
	const isEmbedded = computed(() => props.companyId !== undefined)

	// Al cambiar de empresa, el reporte histórico seleccionado ya no aplica
	watch(companyId, () => {
		selectedHistory.value = null
	})

	const { company: companyInfo } = useCompany(
		{ companyId },
		{ immediate: true }
	)

	const {
		report,
		history,
		historyTotal,
		historyFilters,
		isLoadingHistory,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isGenerating,
		isThrottled,
		error,
		generate,
	} = useAiTravelReport(companyId)

	// Scroll infinito del historial: cerca del fondo, carga la siguiente página
	function onHistoryScroll(event: Event) {
		const el = event.target as HTMLElement
		if (
			el.scrollTop + el.clientHeight >= el.scrollHeight - 120 &&
			hasNextPage?.value &&
			!isFetchingNextPage.value
		) {
			fetchNextPage()
		}
	}

	// ─── Período de generación ───────────────────────────────────────────────────
	// El preset se resuelve a fechas concretas aquí; el backend solo recibe dates.

	const PERIOD_OPTIONS: { label: string; value: AiReportPeriodPreset }[] = [
		{ label: 'Todo', value: 'all' },
		{ label: 'Este mes', value: 'monthly' },
		{ label: 'Trimestre', value: 'quarterly' },
		{ label: 'Este año', value: 'yearly' },
		{ label: 'Personalizado', value: 'custom' },
	]

	const periodPreset = ref<AiReportPeriodPreset>('all')
	const customRange = ref<[string, string] | null>(null)

	const generateDates = computed<[string, string] | null>(() => {
		const today = moment().format('YYYY-MM-DD')
		switch (periodPreset.value) {
			case 'monthly':
				return [moment().startOf('month').format('YYYY-MM-DD'), today]
			case 'quarterly':
				return [
					moment().subtract(2, 'months').startOf('month').format('YYYY-MM-DD'),
					today,
				]
			case 'yearly':
				return [moment().startOf('year').format('YYYY-MM-DD'), today]
			case 'custom':
				return customRange.value
			default:
				return null
		}
	})

	// Enfoque del análisis — presets que el backend inyecta al prompt del agente.
	// La descripción dice en qué DATOS se basa cada uno (mismas métricas para todos;
	// cambia qué prioriza la IA al redactar).
	const FOCUS_OPTIONS: {
		label: string
		value: AiReportFocus
		description: string
	}[] = [
		{
			label: 'General',
			value: 'general',
			description:
				'Análisis balanceado del período: gasto total, tendencia mensual, destinos y la señal más importante a atender.',
		},
		{
			label: 'Ahorro',
			value: 'ahorro',
			description:
				'Se basa en el ahorro vs. tarifa pública de las reservas confirmadas (monto y %). Recomendaciones centradas en gastar menos.',
		},
		{
			label: 'Destinos',
			value: 'destinos',
			description:
				'Se basa en el gasto por ciudad y los hoteles más reservados. Busca oportunidades de tarifas negociadas donde más viaja tu empresa.',
		},
		{
			label: 'Cancelaciones',
			value: 'cancelaciones',
			description:
				'Se basa en las reservas canceladas, su costo y en qué meses se concentran. Recomendaciones para reducirlas.',
		},
		{
			label: 'Anticipación',
			value: 'anticipacion',
			description:
				'Se basa en los días de anticipación entre la solicitud y el check-in, y su efecto en el costo. Recomendaciones de compra anticipada.',
		},
	]

	const focus = ref<AiReportFocus>('general')

	const focusDescription = computed(
		() => FOCUS_OPTIONS.find((o) => o.value === focus.value)?.description ?? ''
	)

	// Etiquetas de lo que se va a generar (banner de regeneración)
	const periodLabel = computed(() => {
		if (periodPreset.value === 'custom' && customRange.value) {
			return `${customRange.value[0]} – ${customRange.value[1]}`
		}
		return (
			PERIOD_OPTIONS.find((o) => o.value === periodPreset.value)?.label ??
			'Todo'
		)
	})

	const focusLabel = computed(
		() => FOCUS_OPTIONS.find((o) => o.value === focus.value)?.label ?? 'General'
	)

	// ─── Medidor semanal de reportes IA + paquetes extra ────────────────────────

	const {
		usage: aiUsage,
		packagesRemaining,
		packagesUsed,
		packagesTotal,
		packageCatalog,
		purchasePackage,
		isPurchasing,
	} = useAiReportUsage(companyId)

	const { hasPermissionUser } = useHasPermissionUser()
	const canBuyPackages = computed(() =>
		hasPermissionUser('subscriptions.manage', '')
	)

	// ai_reports es una feature de tope (limit por plan, no included): locked
	// viene true solo cuando el límite del plan es 0 (hoy, free). Ver
	// usePlanFeature.ts para el criterio limit vs included.
	const { locked: aiReportsLocked, currentPlanCode } = usePlanFeature(
		companyId,
		'ai_reports'
	)
	const isBusinessPlan = computed(() => currentPlanCode.value === 'business')
	const showUpgradeModal = ref(false)

	const showPackagesModal = ref(false)

	// Confirmación explícita del gasto: elegir un paquete solo lo selecciona;
	// el POST sale hasta "Confirmar compra". El cargo va directo al corte de la
	// suscripción, así que un clic accidental no debe comprar.
	const selectedPackage = ref<AiReportPackageOption | null>(null)

	watch(showPackagesModal, (open) => {
		if (!open) selectedPackage.value = null
	})

	function confirmPurchase() {
		if (!selectedPackage.value) return
		purchasePackage(selectedPackage.value.code, {
			onSuccess: () => {
				showPackagesModal.value = false
				selectedPackage.value = null
			},
		})
	}

	// Cuota del plan agotada esta semana (los créditos extra pueden seguir cubriendo)
	const weeklyExhausted = computed(() => aiUsage.value?.remaining === 0)

	// Bloqueado de verdad: sin cuota semanal Y sin créditos de paquete
	const quotaExhausted = computed(
		() => weeklyExhausted.value && packagesRemaining.value === 0
	)

	// % de la cuota del PLAN consumida — manda solo el color del segmento del plan
	const meterPct = computed(() => {
		const u = aiUsage.value
		if (!u || u.limit === null || u.limit === 0) return 0
		return Math.min(100, Math.round((u.used / u.limit) * 100))
	})

	// Barra segmentada: el ancho total representa plan + créditos extra comprados,
	// para distinguir qué consumo salió del plan y cuál de paquetes.
	const meterTotalUnits = computed(
		() => (aiUsage.value?.limit ?? 0) + packagesTotal.value
	)

	const planUsedPct = computed(() => {
		const u = aiUsage.value
		if (!u || u.limit === null || meterTotalUnits.value === 0) return 0
		return Math.min(100, Math.round((u.used / meterTotalUnits.value) * 100))
	})

	const extraUsedPct = computed(() => {
		if (meterTotalUnits.value === 0) return 0
		return Math.min(
			100 - planUsedPct.value,
			Math.round((packagesUsed.value / meterTotalUnits.value) * 100)
		)
	})

	// periodEnd es el último día de la ventana (domingo); el contador reinicia al día siguiente
	const formatPeriodEnd = (iso: string) =>
		moment(iso).add(1, 'day').format('dddd D [de] MMMM')

	const canGenerate = computed(
		() =>
			(periodPreset.value !== 'custom' || !!customRange.value) &&
			!quotaExhausted.value
	)

	// El período/enfoque viven en un modal (en vez de filtros sueltos en la
	// página): generar un reporte es una petición puntual a la IA, no un filtro
	// de vista — el modal lo deja claro.
	const showGenerateModal = ref(false)

	function confirmGenerateFromModal() {
		showGenerateModal.value = false
		runGenerate()
	}

	// ─── Detección de duplicados ────────────────────────────────────────────────
	// Regenerar con el mismo período+enfoque produce la MISMA información con
	// otras palabras (la IA no inventa datos nuevos) y quema cuota. Antes de
	// generar se busca en el historial un reporte con los mismos filtros y se
	// deja decidir: verlo o regenerar de todos modos.

	const duplicateReport = ref<AiTravelReport | null>(null)
	const showDuplicateModal = ref(false)

	// Solo avisa si el duplicado es reciente: más viejo que esto, los datos ya
	// pudieron cambiar (reservas nuevas) y regenerar es legítimo. Los presets se
	// comparan por fechas CONCRETAS, así que "Trimestre" de otro año nunca matchea;
	// esta ventana cubre sobre todo "Todo" (sin fechas) y reportes del día.
	const DUPLICATE_WARNING_HOURS = 24

	function isRecent(item: AiTravelReport): boolean {
		return (
			moment().diff(moment(item.generatedAt), 'hours') < DUPLICATE_WARNING_HOURS
		)
	}

	function sameGenerationParams(item: AiTravelReport): boolean {
		const p = item.params
		const itemDates = p?.dates ?? null
		const itemFocus = p?.focus ?? 'general'
		const currentDates = generateDates.value

		const datesEqual =
			(!itemDates && !currentDates) ||
			(!!itemDates &&
				!!currentDates &&
				itemDates[0] === currentDates[0] &&
				itemDates[1] === currentDates[1])

		return datesEqual && itemFocus === focus.value
	}

	function runGenerate(fresh = false, force = false) {
		if (!canGenerate.value) return

		if (!force) {
			const duplicate = history.value.find(
				(item) => sameGenerationParams(item) && isRecent(item)
			)
			// Aplica también a "Actualizar" viendo ese mismo reporte: regenerarlo
			// recién hecho produce lo mismo con otras palabras y quema cuota.
			if (duplicate) {
				duplicateReport.value = duplicate
				showDuplicateModal.value = true
				return
			}
		}

		generate({ fresh, dates: generateDates.value, focus: focus.value })
	}

	// ¿El duplicado es el reporte que ya está en pantalla? (Actualizar sobre sí mismo)
	const duplicateIsDisplayed = computed(
		() => duplicateReport.value?.id === displayed.value?.id
	)

	function viewDuplicate() {
		if (duplicateReport.value) viewHistory(duplicateReport.value)
		showDuplicateModal.value = false
	}

	function regenerateAnyway() {
		showDuplicateModal.value = false
		// fresh forzado: sin él, el cache del backend devolvería el mismo reporte
		runGenerate(true, true)
	}

	const SEVERITY_OPTIONS: {
		label: string
		value: AiReportSeverity
		color: string
	}[] = [
		{
			label: 'Alta',
			value: 'high',
			color: 'bg-red-100 text-red-700 border-red-200',
		},
		{
			label: 'Media',
			value: 'medium',
			color: 'bg-amber-100 text-amber-700 border-amber-200',
		},
		{
			label: 'Baja',
			value: 'low',
			color: 'bg-blue-100 text-blue-700 border-blue-200',
		},
	]

	function toggleSeverity(val: AiReportSeverity) {
		historyFilters.value.severity =
			historyFilters.value.severity === val ? null : val
	}

	const historyDateRange = ref<[string, string] | null>(null)

	watch(historyDateRange, (range) => {
		historyFilters.value.from = range?.[0] ?? null
		historyFilters.value.to = range?.[1] ?? null
	})

	const REC_OPTIONS = [
		{ label: '1+', value: 1 },
		{ label: '2+', value: 2 },
		{ label: '3+', value: 3 },
	]

	function toggleMinRec(val: number) {
		historyFilters.value.minRecommendations =
			historyFilters.value.minRecommendations === val ? null : val
	}

	function clearHistoryFilters() {
		historyFilters.value = {}
		historyDateRange.value = null
	}

	const aiReportStore = useAiReportStore()
	watch(
		() => aiReportStore.generateRequested,
		(requested) => {
			if (requested) {
				aiReportStore.clearRequest()
				// El botón flotante también es una petición: abre el modal en vez
				// de generar directo con lo que haya quedado seleccionado.
				showGenerateModal.value = true
			}
		}
	)

	const selectedHistory = ref<AiTravelReport | null>(null)
	const isLoadingSelected = ref(false)

	// Deep link desde la notificación: /reports/ai-travel?report={id}&company={id}
	const route = useRoute()

	watch(
		() => route.query.company,
		(companyQuery) => {
			const id = Number(companyQuery)
			if (id) selectedCompanyId.value = id
		},
		{ immediate: true }
	)

	watch(
		[companyId, () => route.query.report],
		async ([cid, reportQuery]) => {
			const id = Number(reportQuery)
			if (!cid || !id || selectedHistory.value?.id === id) return
			isLoadingSelected.value = true
			try {
				selectedHistory.value = await getAiReport(cid, id)
			} catch {
				// reporte no encontrado — se queda la vista normal
			} finally {
				isLoadingSelected.value = false
			}
		},
		{ immediate: true }
	)

	const displayed = computed(() => selectedHistory.value ?? report.value)

	async function viewHistory(item: AiTravelReport) {
		isLoadingSelected.value = true
		try {
			selectedHistory.value = await getAiReport(item.companyId, item.id)
		} catch {
			selectedHistory.value = item
		} finally {
			isLoadingSelected.value = false
		}
	}

	function backToCurrent() {
		selectedHistory.value = null
	}

	marked.setOptions({ breaks: true })

	const SEVERITY_BADGES: Record<string, string> = {
		HIGH: '<span class="severity-badge severity-high">⚠ Prioridad alta</span>',
		MEDIUM:
			'<span class="severity-badge severity-medium">● Prioridad media</span>',
		LOW: '<span class="severity-badge severity-low">○ Prioridad baja</span>',
	}

	const renderedContent = computed(() => {
		if (!displayed.value?.content) return ''
		const html = marked.parse(displayed.value.content) as string
		return html.replace(
			/\[(HIGH|MEDIUM|LOW)\]/g,
			(_, level) => SEVERITY_BADGES[level]
		)
	})

	const formatDate = (iso: string) =>
		moment(iso).format('D [de] MMMM [de] YYYY[,] HH:mm')

	const formatRelative = (iso: string) => moment(iso).fromNow()

	const formatMoney = (val: number, currency = 'MXN') =>
		new Intl.NumberFormat('es-MX', {
			style: 'currency',
			currency,
			maximumFractionDigits: 0,
		}).format(val)

	const formatNum = (val: number, decimals = 0) =>
		new Intl.NumberFormat('es-MX', { maximumFractionDigits: decimals }).format(
			val
		)


	const toChartData = (c: AiChart) => ({
		labels: c.labels,
		datasets: c.datasets.map((d, i) => ({
			...d,
			backgroundColor:
				i === 0 ? 'rgba(2, 31, 88, 0.75)' : 'rgba(37, 60, 112, 0.6)',
			borderColor: i === 0 ? '#021F58' : '#253C70',
			borderWidth: 2,
			pointRadius: 3,
			tension: 0.3,
		})),
	})

	const makeChartOpts = (labels: string[], maxLen = 14) => ({
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: { display: false },
			tooltip: {
				mode: 'index' as const,
				intersect: false,
				callbacks: {
					title: (items: { dataIndex: number }[]) =>
						labels[items[0]?.dataIndex] ?? '',
				},
			},
		},
		scales: {
			x: {
				ticks: {
					font: { size: 10 },
					maxRotation: 45,
					callback: (_val: unknown, idx: number) => {
						const label = labels[idx] ?? ''
						return label.length > maxLen ?
								label.substring(0, maxLen) + '…'
							:	label
					},
				},
				grid: { display: false },
			},
			y: { ticks: { font: { size: 10 } }, grid: { color: 'rgba(0,0,0,0.04)' } },
		},
	})

	const chartOpts = makeChartOpts([])

	const reportRef = ref<HTMLElement | null>(null)
	const isExporting = ref(false)

	const exportPdf = async () => {
		if (!reportRef.value || !displayed.value) return
		isExporting.value = true
		try {
			const canvas = await html2canvas(reportRef.value, {
				scale: 2,
				useCORS: true,
				logging: false,
				backgroundColor: '#ffffff',
			})

			const doc = new jsPDF('p', 'mm', 'a4')
			const pageWidth = 210
			const pageHeight = 297
			const margin = 10
			const logoSize = 14

			// Header — company logo only (no text fallback)
			const companyLogoSrc = companyInfo.value?.logo
			let headerH = 0

			if (companyLogoSrc) {
				try {
					doc.addImage(
						companyLogoSrc,
						'PNG',
						pageWidth - margin - logoSize,
						3,
						logoSize,
						logoSize
					)
					headerH = logoSize + 4
				} catch {
					// logo failed silently — no text fallback
				}
			}

			// Report content image
			const imgData = canvas.toDataURL('image/png')
			const availW = pageWidth - margin * 2
			const imgH = (canvas.height * availW) / canvas.width
			let yPos = headerH > 0 ? headerH : margin

			if (yPos + imgH <= pageHeight - margin) {
				doc.addImage(imgData, 'PNG', margin, yPos, availW, imgH)
			} else {
				// Multi-page: slice image across pages
				const pageContentH = pageHeight - margin - yPos
				let srcY = 0
				let remaining = imgH

				while (remaining > 0) {
					const sliceH = Math.min(remaining, pageContentH)
					const sliceCanvas = document.createElement('canvas')
					const ratio = canvas.width / availW
					sliceCanvas.width = canvas.width
					sliceCanvas.height = sliceH * ratio
					const ctx = sliceCanvas.getContext('2d')!
					ctx.drawImage(
						canvas,
						0,
						srcY * ratio,
						canvas.width,
						sliceH * ratio,
						0,
						0,
						canvas.width,
						sliceH * ratio
					)
					doc.addImage(
						sliceCanvas.toDataURL('image/png'),
						'PNG',
						margin,
						yPos,
						availW,
						sliceH
					)
					srcY += sliceH
					remaining -= sliceH
					if (remaining > 0) {
						doc.addPage()
						yPos = margin
					}
				}
			}

			const filename = `reporte-ia-${(companyInfo.value?.name ?? user.value?.companyName ?? 'empresa').toLowerCase().replace(/\s+/g, '-')}-${moment().format('YYYY-MM-DD')}.pdf`
			doc.save(filename)
		} finally {
			isExporting.value = false
		}
	}
</script>

<template>
	<div
		:class="isEmbedded ? 'py-2' : 'max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8'"
	>
		<!-- Plan sin cupo de reportes IA (limit 0, hoy solo free): sin acceso al contenido -->
		<div
			v-if="aiReportsLocked"
			class="flex flex-col items-center justify-center text-center gap-3 py-24"
		>
			<p class="text-sm font-semibold text-gray-700 max-w-sm">
				Tu plan actual no incluye reportes de viajes con IA. Start incluye 3
				por semana, Pro 10 y Business+ ilimitados.
			</p>
			<button
				type="button"
				class="mt-1 px-4 py-2 rounded-lg bg-[#021F58] text-white text-sm font-medium hover:opacity-90 transition-opacity"
				@click="showUpgradeModal = true"
			>
				Ver planes
			</button>
		</div>

		<template v-else>
			<!-- Header -->
		<div class="flex items-start justify-between mb-6">
			<div v-if="!isEmbedded">
				<h1 class="text-2xl font-bold text-gray-900">Reporte de viajes IA</h1>
				<p class="text-sm text-gray-500 mt-1">
					Análisis inteligente de los viajes de tu empresa
				</p>
			</div>
			<div
				v-else
				class="text-sm text-gray-500 self-center"
			>
				Análisis inteligente de los viajes de la empresa
			</div>

			<div class="flex items-center gap-2">
				<!-- Descargar PDF -->
				<button
					v-if="displayed"
					type="button"
					:disabled="isExporting"
					class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
					@click="exportPdf"
				>
					<svg
						v-if="isExporting"
						class="animate-spin w-4 h-4"
						fill="none"
						viewBox="0 0 24 24"
					>
						<circle
							class="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							stroke-width="4"
						/>
						<path
							class="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
						/>
					</svg>
					<svg
						v-else
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
						/>
					</svg>
					{{ isExporting ? 'Exportando...' : 'Descargar PDF' }}
				</button>

				<!-- Actualizar (visible también viendo un histórico: genera uno nuevo) -->
				<button
					v-if="displayed"
					type="button"
					:disabled="isGenerating || isThrottled || !canGenerate"
					class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
					title="Forzar regeneración"
					@click="runGenerate(true)"
				>
					<svg
						class="w-4 h-4"
						:class="isGenerating ? 'animate-spin' : ''"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
						/>
					</svg>
					Actualizar
				</button>

				<!-- Nuevo reporte con otro período/enfoque (abre el modal de petición) -->
				<button
					v-if="displayed"
					type="button"
					:disabled="isGenerating || isThrottled"
					class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#021F58] text-white hover:bg-[#1C3A7A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					@click="showGenerateModal = true"
				>
					<svg
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M12 4v16m8-8H4"
						/>
					</svg>
					Nuevo reporte
				</button>

				<!-- Generar (abre el modal de petición) -->
				<button
					v-if="!displayed"
					type="button"
					:disabled="isGenerating || isThrottled"
					class="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold bg-[#021F58] text-white hover:bg-[#1C3A7A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					@click="showGenerateModal = true"
				>
					<svg
						v-if="isGenerating"
						class="animate-spin w-4 h-4"
						fill="none"
						viewBox="0 0 24 24"
					>
						<circle
							class="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							stroke-width="4"
						/>
						<path
							class="opacity-75"
							fill="currentColor"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
						/>
					</svg>
					<svg
						v-else
						class="w-4 h-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M13 10V3L4 14h7v7l9-11h-7z"
						/>
					</svg>
					{{ isGenerating ? 'Generando...' : 'Generar reporte' }}
				</button>
			</div>
		</div>

		<!-- Medidor semanal + créditos extra -->
		<div
			v-if="aiUsage && aiUsage.limit !== null"
			class="mb-4 flex flex-wrap items-center gap-3"
		>
			<span class="text-xs font-medium text-gray-400"
				>Reportes esta semana</span
			>
			<div class="w-40 h-1.5 rounded-full bg-gray-100 overflow-hidden flex">
				<!-- Segmento del plan (azul → ámbar 80% → rojo agotado) -->
				<div
					class="h-full transition-all duration-300"
					:class="
						quotaExhausted ? 'bg-red-500'
						: meterPct >= 80 ? 'bg-amber-500'
						: 'bg-[#021F58]'
					"
					:style="{ width: `${planUsedPct}%` }"
					title="Consumido de la cuota del plan"
				></div>
				<!-- Segmento de créditos extra consumidos (paquetes) -->
				<div
					v-if="extraUsedPct > 0"
					class="h-full bg-emerald-500 transition-all duration-300"
					:style="{ width: `${extraUsedPct}%` }"
					title="Consumido de paquetes extra"
				></div>
			</div>
			<span
				class="text-xs font-semibold"
				:class="quotaExhausted ? 'text-red-600' : 'text-gray-600'"
				title="Consumo de la cuota del plan"
			>
				{{ aiUsage.used }} / {{ aiUsage.limit }}
			</span>
			<span
				v-if="packagesRemaining > 0"
				class="text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
				title="Créditos de paquetes extra disponibles; se usan cuando se acaba la cuota semanal"
			>
				+{{ packagesRemaining }} disponibles
			</span>
			<span class="text-xs text-gray-400">
				· se reinicia el {{ formatPeriodEnd(aiUsage.periodEnd) }}
			</span>
			<span
				v-if="weeklyExhausted && !quotaExhausted"
				class="text-xs font-medium text-emerald-700"
			>
				Usando reportes extra
			</span>
			<span
				v-else-if="quotaExhausted"
				class="text-xs font-medium text-red-600"
			>
				Límite semanal alcanzado
			</span>
			<button
				v-if="canBuyPackages && packageCatalog.length && !isBusinessPlan"
				type="button"
				class="inline-flex items-center gap-1 text-xs font-medium rounded-full border px-2.5 py-0.5 transition-colors"
				:class="
					quotaExhausted ?
						'border-[#021F58] bg-[#021F58] text-white hover:bg-[#1C3A7A]'
					:	'border-gray-200 text-gray-500 hover:border-[#021F58] hover:text-[#021F58]'
				"
				@click="showPackagesModal = true"
			>
				<span class="text-sm leading-none">+</span>
				Agregar reportes
			</button>
			<a
				v-if="isBusinessPlan"
				:href="CONTACTS.sales.url"
				target="_blank"
				rel="noopener noreferrer"
				class="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-[#021F58] transition-colors"
			>
				¿Necesitas reportes adicionales? Contacta a {{ CONTACTS.sales.name }}
			</a>
		</div>

		<!-- Modal: pedir un reporte nuevo (período + enfoque) -->
		<ModalComponent
			v-model="showGenerateModal"
			class="max-w-lg"
		>
			<template #modalBody>
				<div class="p-6">
					<div class="flex items-start justify-between mb-1">
						<div>
							<h3 class="text-lg font-bold text-gray-900">
								Nuevo reporte de viajes IA
							</h3>
							<p class="text-sm text-gray-500 mt-0.5">
								Elige qué período y enfoque quieres que la IA analice.
							</p>
						</div>
						<button
							type="button"
							class="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
							@click="showGenerateModal = false"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>

					<!-- Período -->
					<div class="mt-5">
						<p class="text-xs font-semibold text-gray-700 mb-2">
							Período a analizar
						</p>
						<div class="flex flex-wrap gap-2">
							<button
								v-for="opt in PERIOD_OPTIONS"
								:key="opt.value"
								type="button"
								class="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
								:class="
									periodPreset === opt.value ?
										'bg-[#021F58] text-white border-[#021F58]'
									:	'border-gray-200 text-gray-500 hover:border-gray-300 bg-white'
								"
								@click="periodPreset = opt.value"
							>
								{{ opt.label }}
							</button>
						</div>
						<div
							v-if="periodPreset === 'custom'"
							class="mt-2"
						>
							<VueDatePicker
								v-model="customRange"
								range
								:enable-time-picker="false"
								auto-apply
								locale="es"
								format="yyyy-MM-dd"
								model-type="format"
								:teleport="true"
								placeholder="Desde – Hasta"
								:hide-input-icon="true"
								:input-class-name="'!text-xs !h-8 !rounded-full !border-gray-200 !px-3 !py-0 hover:!border-gray-300'"
							/>
						</div>
					</div>

					<!-- Enfoque -->
					<div class="mt-5">
						<p class="text-xs font-semibold text-gray-700 mb-2">
							Enfoque del análisis
						</p>
						<div class="flex flex-wrap gap-2">
							<button
								v-for="opt in FOCUS_OPTIONS"
								:key="opt.value"
								type="button"
								:title="opt.description"
								class="px-3 py-1.5 rounded-full text-xs font-medium border transition-all"
								:class="
									focus === opt.value ?
										'bg-[#021F58] text-white border-[#021F58]'
									:	'border-gray-200 text-gray-500 hover:border-gray-300 bg-white'
								"
								@click="focus = opt.value"
							>
								{{ opt.label }}
							</button>
						</div>
						<p class="mt-2 text-xs text-gray-400 leading-relaxed">
							{{ focusDescription }}
						</p>
					</div>

					<!-- Cuota restante, si aplica -->
					<p
						v-if="quotaExhausted"
						class="mt-4 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2"
					>
						Límite semanal alcanzado — no puedes generar otro reporte hasta que
						se reinicie tu cuota{{
							packagesRemaining === 0 ? ' o compres reportes extra' : ''
						}}.
					</p>

					<div class="mt-6 flex justify-end gap-2">
						<button
							type="button"
							class="px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-colors"
							@click="showGenerateModal = false"
						>
							Cancelar
						</button>
						<button
							type="button"
							:disabled="isGenerating || isThrottled || !canGenerate"
							class="inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold bg-[#021F58] text-white hover:bg-[#1C3A7A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
							@click="confirmGenerateFromModal"
						>
							{{ isGenerating ? 'Generando...' : 'Generar reporte' }}
						</button>
					</div>
				</div>
			</template>
		</ModalComponent>

		<!-- Modal de paquetes extra -->
		<ModalComponent
			v-model="showPackagesModal"
			class="max-w-md"
		>
			<template #modalBody>
				<div class="p-6">
					<div class="flex items-start justify-between mb-1">
						<h3 class="text-lg font-bold text-gray-900">Reportes IA extra</h3>
						<button
							type="button"
							class="text-gray-400 hover:text-gray-600 transition-colors"
							@click="showPackagesModal = false"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M6 18L18 6M6 6l12 12"
								/>
							</svg>
						</button>
					</div>
					<p class="text-sm text-gray-500 mb-4">
						El cargo se suma a tu suscripción en el próximo corte — sin cobro
						inmediato. Los reportes extra se usan cuando se acaba tu cuota
						semanal y vencen al final del ciclo.
					</p>

					<!-- Paso 1: elegir paquete (no compra todavía) -->
					<div
						v-if="!selectedPackage"
						class="space-y-2"
					>
						<button
							v-for="pkg in packageCatalog"
							:key="pkg.code"
							type="button"
							class="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-gray-200 hover:border-[#021F58] hover:bg-[#021F58]/5 transition-colors text-left"
							@click="selectedPackage = pkg"
						>
							<div>
								<p class="text-sm font-semibold text-gray-800">
									{{ pkg.label }}
								</p>
								<p class="text-xs text-gray-400">
									${{ (pkg.priceMxn / pkg.reports).toFixed(0) }} MXN por reporte
								</p>
							</div>
							<span class="text-sm font-bold text-[#021F58]">
								${{ pkg.priceMxn }} MXN
							</span>
						</button>
					</div>

					<!-- Paso 2: confirmar el gasto antes de postear la compra -->
					<div
						v-else
						class="rounded-xl border border-gray-200 p-4"
					>
						<p class="text-sm font-semibold text-gray-800">
							{{ selectedPackage.label }}
						</p>
						<p class="text-sm text-gray-500 mt-1">
							Se agregarán
							<span class="font-bold text-[#021F58]"
								>${{ selectedPackage.priceMxn }} MXN</span
							>
							al próximo corte de tu suscripción. ¿Confirmas la compra?
						</p>
						<div class="flex gap-2 justify-end mt-4">
							<button
								type="button"
								:disabled="isPurchasing"
								class="px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition-colors"
								@click="selectedPackage = null"
							>
								Cancelar
							</button>
							<button
								type="button"
								:disabled="isPurchasing"
								class="px-4 py-2 rounded-xl text-sm font-semibold bg-[#021F58] text-white hover:bg-[#1C3A7A] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
								@click="confirmPurchase"
							>
								{{ isPurchasing ? 'Comprando…' : 'Confirmar compra' }}
							</button>
						</div>
					</div>

					<p
						v-if="packagesRemaining > 0"
						class="mt-4 text-xs text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2"
					>
						Tienes {{ packagesRemaining }} reporte{{
							packagesRemaining === 1 ? '' : 's'
						}}
						extra sin usar.
					</p>
				</div>
			</template>
		</ModalComponent>

		<!-- Modal de upgrade a Pro (plan free) -->
		<PlanGateModal v-model:open="showUpgradeModal" />

		<!-- Modal: ya existe un reporte con estos filtros -->
		<ModalComponent
			v-model="showDuplicateModal"
			class="max-w-md"
		>
			<template #modalBody>
				<div
					v-if="duplicateReport"
					class="p-6"
				>
					<h3 class="text-lg font-bold text-gray-900 mb-1">
						{{
							duplicateIsDisplayed
								? 'Este reporte es reciente'
								: 'Ya tienes este reporte'
						}}
					</h3>
					<p class="text-sm text-gray-500 mb-3">
						<template v-if="duplicateIsDisplayed">
							El reporte que estás viendo se generó
						</template>
						<template v-else>
							Generaste un reporte con el mismo período y enfoque
						</template>
						<span class="font-semibold text-gray-700">{{
							formatRelative(duplicateReport.generatedAt)
						}}</span
						><template v-if="duplicateReport.requestedBy">
							(por {{ duplicateReport.requestedBy.name }})</template
						>.
					</p>
					<p
						class="text-xs text-gray-500 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-4"
					>
						Regenerar con los mismos filtros produce la misma información con
						otras palabras — la IA no tiene datos nuevos que analizar — y
						consume un reporte de tu cuota semanal.
					</p>
					<div class="flex gap-2 justify-end">
						<button
							type="button"
							class="px-4 py-2 rounded-xl text-sm font-medium border border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50 transition-colors"
							@click="regenerateAnyway"
						>
							Generar de nuevo
						</button>
						<button
							v-if="!duplicateIsDisplayed"
							type="button"
							class="px-4 py-2 rounded-xl text-sm font-semibold bg-[#021F58] text-white hover:bg-[#1C3A7A] transition-colors"
							@click="viewDuplicate"
						>
							Ver ese reporte
						</button>
						<button
							v-else
							type="button"
							class="px-4 py-2 rounded-xl text-sm font-semibold bg-[#021F58] text-white hover:bg-[#1C3A7A] transition-colors"
							@click="showDuplicateModal = false"
						>
							Conservar este
						</button>
					</div>
				</div>
			</template>
		</ModalComponent>

		<!-- Alerts -->
		<div
			v-if="isThrottled"
			class="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700"
		>
			Límite de 5 reportes por minuto alcanzado. Espera un momento antes de
			intentar de nuevo.
		</div>
		<div
			v-else-if="error && !isGenerating"
			class="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
		>
			{{ error }}
		</div>

		<!-- Layout 2 col -->
		<div class="flex flex-col lg:flex-row gap-5">
			<!-- ── Main ── -->
			<div class="flex-1 min-w-0">
				<!-- Loading historical report -->
				<div
					v-if="isLoadingSelected"
					class="rounded-xl border border-gray-200 bg-white p-10 flex flex-col items-center gap-4 text-center"
				>
					<div class="relative w-10 h-10">
						<div
							class="absolute inset-0 rounded-full border-4 border-[#021F58]/10"
						/>
						<div
							class="absolute inset-0 rounded-full border-4 border-t-[#021F58] animate-spin"
						/>
					</div>
					<p class="text-sm text-gray-400">Cargando reporte...</p>
				</div>

				<!-- Generating -->
				<div
					v-else-if="isGenerating && !report"
					class="rounded-xl border border-gray-200 bg-white p-10 flex flex-col items-center gap-4 text-center"
				>
					<div class="relative w-14 h-14">
						<div
							class="absolute inset-0 rounded-full border-4 border-[#021F58]/10"
						/>
						<div
							class="absolute inset-0 rounded-full border-4 border-t-[#021F58] animate-spin"
						/>
					</div>
					<div>
						<p class="font-semibold text-gray-900">Generando reporte</p>
						<p class="text-sm text-gray-400 mt-1">
							La IA está analizando los viajes. Esto puede tardar unos segundos.
						</p>
					</div>
				</div>

				<!-- Empty -->
				<div
					v-else-if="!isGenerating && !displayed"
					class="rounded-xl border border-dashed border-gray-200 bg-white p-12 flex flex-col items-center gap-3 text-center"
				>
					<svg
						class="w-12 h-12 text-gray-200"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="1.5"
							d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
						/>
					</svg>
					<div>
						<p class="font-semibold text-gray-700">Sin reporte generado</p>
						<p class="text-sm text-gray-400 mt-1">
							Haz clic en "Generar reporte" para que la IA analice los viajes.
						</p>
					</div>
				</div>

				<!-- Report card (con aviso encima cuando se está regenerando) -->
				<template v-else-if="displayed">
					<div
						v-if="isGenerating"
						class="mb-3 rounded-xl border border-[#021F58]/20 bg-[#021F58]/5 px-4 py-3 flex items-center gap-3"
					>
						<div class="relative w-5 h-5 shrink-0">
							<div
								class="absolute inset-0 rounded-full border-2 border-[#021F58]/15"
							/>
							<div
								class="absolute inset-0 rounded-full border-2 border-t-[#021F58] animate-spin"
							/>
						</div>
						<div class="min-w-0">
							<p class="text-sm font-semibold text-[#021F58]">
								Generando nuevo reporte — {{ periodLabel }} · Enfoque
								{{ focusLabel }}
							</p>
							<p class="text-xs text-gray-500">
								El reporte que estás viendo se reemplazará cuando termine.
							</p>
						</div>
					</div>

					<div
						ref="reportRef"
						class="bg-white rounded-xl border border-gray-200 overflow-hidden transition-opacity"
						:class="isGenerating ? 'opacity-60' : ''"
					>
						<!-- Meta header -->
						<div
							class="px-6 py-4 border-b border-gray-100 flex items-center justify-between"
						>
							<div class="flex items-center gap-3">
								<!-- Company logo / fallback -->
								<div class="shrink-0">
									<img
										v-if="companyInfo?.logo"
										:src="companyInfo?.logo"
										:alt="companyInfo?.name ?? user?.companyName"
										class="w-9 h-9 rounded-xl object-cover ring-1 ring-gray-100"
									/>
									<div
										v-else
										class="w-9 h-9 rounded-xl bg-[#021F58] flex items-center justify-center"
									>
										<span class="text-[13px] font-bold text-white leading-none">
											{{
												(companyInfo?.name ?? user?.companyName ?? 'IA')
													.substring(0, 2)
													.toUpperCase()
											}}
										</span>
									</div>
								</div>
								<div class="min-w-0">
									<p class="text-sm font-semibold text-gray-900">
										{{
											companyInfo?.name ??
											user?.companyName ??
											'Análisis generado por IA'
										}}
									</p>
									<p class="text-xs text-gray-400">
										{{ formatRelative(displayed.generatedAt) }} ·
										{{ formatDate(displayed.generatedAt) }}
										<template v-if="displayed.requestedBy">
											· Generado por
											<span class="font-medium text-gray-500">{{
												displayed.requestedBy.name
											}}</span>
										</template>
									</p>
								</div>
							</div>

							<div class="flex items-center gap-2">
								<span
									v-if="
										displayed.period &&
										displayed.period !== 'periodo seleccionado'
									"
									class="text-xs font-medium text-gray-500 bg-white border border-gray-200 px-3 py-1 rounded-full shadow-sm"
								>
									{{ displayed.period }}
								</span>
							</div>
						</div>

						<!-- KPIs -->
						<div
							v-if="displayed.kpis"
							class="px-6 py-4 border-b border-gray-100"
						>
							<div class="grid grid-cols-2 sm:grid-cols-4 gap-3">
								<div class="bg-gray-50 rounded-xl p-4">
									<p
										class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1"
									>
										Gasto total
									</p>
									<p class="text-lg font-bold text-gray-900">
										{{
											formatMoney(
												displayed.kpis.totalSpend,
												displayed.meta?.currency
											)
										}}
									</p>
								</div>
								<div class="bg-gray-50 rounded-xl p-4">
									<p
										class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1"
									>
										Reservas
									</p>
									<p class="text-lg font-bold text-gray-900">
										{{ formatNum(displayed.kpis.totalTrips) }}
									</p>
								</div>
								<div class="bg-gray-50 rounded-xl p-4">
									<p
										class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1"
									>
										Promedio / reserva
									</p>
									<p class="text-lg font-bold text-gray-900">
										{{
											formatMoney(
												displayed.kpis.averagePerTrip,
												displayed.meta?.currency
											)
										}}
									</p>
								</div>
								<div class="bg-gray-50 rounded-xl p-4">
									<p
										class="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1"
									>
										Cancelación
									</p>
									<p class="text-lg font-bold text-gray-900">
										{{ formatNum(displayed.kpis.cancellationRate, 1) }}%
									</p>
								</div>
							</div>
						</div>

						<!-- Charts -->
						<div
							v-if="displayed.charts"
							class="px-6 py-5 border-b border-gray-100 space-y-4"
						>
							<!-- Fila 1: Destinos + Hoteles -->
							<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div class="bg-gray-50 rounded-xl p-4">
									<p class="text-sm font-semibold text-gray-700 mb-3">
										{{ displayed.charts.topDestinations.title }}
									</p>
									<div style="height: 260px">
										<Bar
											:data="toChartData(displayed.charts.topDestinations)"
											:options="
												makeChartOpts(
													displayed.charts.topDestinations.labels,
													16
												)
											"
										/>
									</div>
								</div>
								<div class="bg-gray-50 rounded-xl p-4">
									<p class="text-sm font-semibold text-gray-700 mb-3">
										{{ displayed.charts.favoriteHotels.title }}
									</p>
									<div style="height: 260px">
										<Bar
											:data="toChartData(displayed.charts.favoriteHotels)"
											:options="
												makeChartOpts(
													displayed.charts.favoriteHotels.labels,
													10
												)
											"
										/>
									</div>
								</div>
							</div>
							<!-- Fila 2: Tendencia de gasto full width -->
							<div class="bg-gray-50 rounded-xl p-4">
								<p class="text-sm font-semibold text-gray-700 mb-3">
									{{ displayed.charts.spendTrend.title }}
								</p>
								<div style="height: 260px">
									<Line
										:data="toChartData(displayed.charts.spendTrend)"
										:options="makeChartOpts(displayed.charts.spendTrend.labels)"
									/>
								</div>
							</div>
						</div>

						<!-- Análisis temporal -->
						<div
							v-if="displayed.analisisTemporal"
							class="px-6 py-4 border-b border-gray-100"
						>
							<div class="flex items-center gap-2 mb-2">
								<span
									class="material-symbols-outlined text-[16px] text-[#021F58]"
									>calendar_month</span
								>
								<p class="text-xs font-semibold text-gray-700">
									Análisis temporal
								</p>
							</div>
							<p class="text-sm text-gray-600 leading-relaxed mb-2">
								{{ displayed.analisisTemporal.tendencia }}
							</p>
							<div class="flex flex-wrap gap-x-4 gap-y-1">
								<span class="text-xs text-gray-500">
									<span class="font-medium text-emerald-600"
										>↑ Mayor gasto:</span
									>
									{{ displayed.analisisTemporal.mesMayorGasto }}
								</span>
								<span class="text-xs text-gray-500">
									<span class="font-medium text-sky-600">↓ Menor gasto:</span>
									{{ displayed.analisisTemporal.mesMenorGasto }}
								</span>
								<span
									v-if="displayed.analisisTemporal.observacionAnual"
									class="text-xs text-gray-400 italic"
								>
									{{ displayed.analisisTemporal.observacionAnual }}
								</span>
							</div>
						</div>

						<!-- Alertas -->
						<div
							v-if="displayed.alertas?.length"
							class="px-6 py-4 border-b border-gray-100"
						>
							<div class="flex items-center gap-2 mb-3">
								<span
									class="material-symbols-outlined text-[16px] text-amber-500"
									>warning</span
								>
								<p class="text-xs font-semibold text-gray-700">Alertas</p>
							</div>
							<div class="space-y-2">
								<div
									v-for="(alerta, i) in displayed.alertas"
									:key="i"
									class="rounded-lg px-3 py-2.5 border"
									:class="{
										'bg-red-50 border-red-100': alerta.severidad === 'high',
										'bg-amber-50 border-amber-100':
											alerta.severidad === 'medium',
										'bg-blue-50 border-blue-100': alerta.severidad === 'low',
									}"
								>
									<div class="flex items-start justify-between gap-2">
										<p class="text-sm font-semibold text-gray-800 leading-snug">
											{{ alerta.titulo }}
										</p>
										<span
											class="shrink-0 text-[11px] font-semibold px-1.5 py-0.5 rounded"
											:class="{
												'bg-red-100 text-red-700': alerta.severidad === 'high',
												'bg-amber-100 text-amber-700':
													alerta.severidad === 'medium',
												'bg-blue-100 text-blue-700': alerta.severidad === 'low',
											}"
										>
											{{
												alerta.severidad === 'high' ? 'Alta'
												: alerta.severidad === 'medium' ? 'Media'
												: 'Baja'
											}}
										</span>
									</div>
									<p class="text-xs text-gray-500 mt-1 leading-relaxed">
										{{ alerta.detalle }}
									</p>
								</div>
							</div>
						</div>

						<!-- Recomendaciones -->
						<div
							v-if="displayed.recomendaciones?.length"
							class="px-6 py-4 border-b border-gray-100"
						>
							<div class="flex items-center gap-2 mb-3">
								<span
									class="material-symbols-outlined text-[16px] text-emerald-600"
									>lightbulb</span
								>
								<p class="text-xs font-semibold text-gray-700">
									Recomendaciones
								</p>
							</div>
							<div class="space-y-2">
								<div
									v-for="(rec, i) in displayed.recomendaciones"
									:key="i"
									class="bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2.5"
								>
									<p class="text-sm font-semibold text-gray-800">
										{{ rec.titulo }}
									</p>
									<p class="text-xs text-gray-500 mt-1 leading-relaxed">
										{{ rec.detalle }}
									</p>
									<p
										v-if="rec.ahorroEstimado"
										class="text-xs font-medium text-emerald-700 mt-1.5"
									>
										Ahorro estimado: {{ rec.ahorroEstimado }}
									</p>
								</div>
							</div>
						</div>

						<!-- Ahorro -->
						<div
							v-if="displayed.ahorroTexto"
							class="px-6 py-4 border-b border-gray-100"
						>
							<div class="flex items-center gap-2 mb-2">
								<span
									class="material-symbols-outlined text-[16px] text-[#021F58]"
									>savings</span
								>
								<p class="text-xs font-semibold text-gray-700">
									Análisis de ahorro
								</p>
							</div>
							<p class="text-sm text-gray-600 leading-relaxed">
								{{ displayed.ahorroTexto }}
							</p>
						</div>

						<!-- Data gaps -->
						<div
							v-if="displayed.dataGaps?.length"
							class="px-6 py-4 border-b border-gray-100"
						>
							<div class="flex items-center gap-2 mb-2">
								<span
									class="material-symbols-outlined text-[16px] text-gray-400"
									>info</span
								>
								<p class="text-xs font-semibold text-gray-700">
									Datos no disponibles
								</p>
							</div>
							<ul class="space-y-1">
								<li
									v-for="(gap, i) in displayed.dataGaps"
									:key="i"
									class="text-xs text-gray-500 flex items-start gap-1.5"
								>
									<span class="text-gray-300 mt-0.5">•</span>
									{{ gap }}
								</li>
							</ul>
						</div>

						<!-- Markdown content -->
						<div
							v-if="renderedContent"
							class="px-6 py-5 ai-report-content"
							v-html="renderedContent"
						/>

						<!-- Summary cuando no hay content completo (items del historial) -->
						<div
							v-else-if="displayed.summary || displayed.shortSummary"
							class="px-6 py-5"
						>
							<p class="text-sm text-gray-600 leading-relaxed">
								{{ displayed.summary ?? displayed.shortSummary }}
							</p>
						</div>
					</div>
				</template>
			</div>

			<!-- ── Sidebar historial ── -->
			<div class="lg:w-72 shrink-0 lg:sticky lg:top-8 lg:self-start">
				<div
					class="bg-white rounded-xl border border-gray-200 overflow-hidden flex flex-col max-h-[calc(100vh-6rem)]"
				>
					<!-- Header -->
					<div
						class="px-4 py-3 border-b border-gray-100 flex items-center justify-between shrink-0"
					>
						<p class="text-sm font-semibold text-gray-900">Historial</p>
						<span
							v-if="historyTotal"
							class="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full"
							title="Total de reportes generados"
						>
							{{ historyTotal }}
						</span>
					</div>

					<!-- Filtros -->
					<div class="px-3 py-2.5 border-b border-gray-100 space-y-2 shrink-0">
						<!-- Severidad -->
						<div class="flex items-center gap-1.5 flex-wrap">
							<button
								v-for="opt in SEVERITY_OPTIONS"
								:key="opt.value"
								type="button"
								class="px-2.5 py-1 rounded-full text-xs font-medium border transition-all"
								:class="
									historyFilters.severity === opt.value ?
										opt.color
									:	'border-gray-200 text-gray-400 hover:border-gray-300'
								"
								@click="toggleSeverity(opt.value)"
							>
								{{ opt.label }}
							</button>
						</div>
						<!-- Fechas VueDatePicker -->
						<VueDatePicker
							v-model="historyDateRange"
							range
							:enable-time-picker="false"
							auto-apply
							locale="es"
							format="yyyy-MM-dd"
							model-type="format"
							:teleport="true"
							placeholder="Desde – Hasta"
							:hide-input-icon="true"
							:input-class-name="'!text-[11px] !h-7 !rounded-lg !border-gray-200 !px-2 !py-0 hover:!border-gray-300'"
						/>
						<!-- Recomendaciones mínimas -->
						<div class="flex items-center gap-1.5">
							<span class="text-xs text-gray-400">Recom. mín.</span>
							<button
								v-for="opt in REC_OPTIONS"
								:key="opt.value"
								type="button"
								class="px-2.5 py-1 rounded-full text-xs font-medium border transition-all"
								:class="
									historyFilters.minRecommendations === opt.value ?
										'bg-[#021F58] text-white border-[#021F58]'
									:	'border-gray-200 text-gray-400 hover:border-gray-300'
								"
								@click="toggleMinRec(opt.value)"
							>
								{{ opt.label }}
							</button>
						</div>
						<!-- Reset -->
						<button
							v-if="
								historyFilters.severity ||
								historyFilters.from ||
								historyFilters.to ||
								historyFilters.minRecommendations
							"
							type="button"
							class="text-xs text-gray-400 hover:text-gray-600 transition-colors"
							@click="clearHistoryFilters"
						>
							Limpiar filtros
						</button>
					</div>

					<!-- Loading -->
					<div
						v-if="isLoadingHistory"
						class="p-4 space-y-2 animate-pulse"
					>
						<div
							v-for="i in 3"
							:key="i"
							class="h-16 rounded-lg bg-gray-100"
						/>
					</div>

					<!-- Empty -->
					<div
						v-else-if="history.length === 0"
						class="px-4 py-8 text-center"
					>
						<p class="text-xs text-gray-400">Sin reportes</p>
					</div>

					<!-- List (scroll infinito) -->
					<div
						v-else
						class="divide-y divide-gray-50 overflow-y-auto flex-1"
						@scroll.passive="onHistoryScroll"
					>
						<button
							v-for="item in history"
							:key="item.id"
							type="button"
							class="w-full px-4 py-3.5 text-left transition-colors border-l-2"
							:class="
								selectedHistory?.id === item.id ?
									'bg-[#021F58]/5 border-l-[#021F58]'
								:	'border-l-transparent hover:bg-gray-50'
							"
							@click="viewHistory(item)"
						>
							<p
								class="text-sm leading-relaxed line-clamp-3"
								:class="
									selectedHistory?.id === item.id ?
										'text-[#021F58] font-medium'
									:	'text-gray-700'
								"
							>
								{{ item.shortSummary ?? 'Reporte generado' }}
							</p>
							<p class="text-xs text-gray-400 mt-1">
								{{ formatRelative(item.generatedAt) }}
								<span class="text-gray-300 mx-1">·</span>
								{{ formatDate(item.generatedAt) }}
							</p>
							<p
								v-if="item.requestedBy"
								class="text-xs text-gray-400 mt-0.5"
							>
								Por
								<span class="font-medium text-gray-500">{{
									item.requestedBy.name
								}}</span>
							</p>
							<div
								v-if="item.alerts?.total"
								class="flex items-center gap-1 mt-1.5 flex-wrap"
							>
								<span
									v-if="item.alerts.high"
									class="text-[11px] font-semibold px-2 py-0.5 rounded bg-red-100 text-red-600"
									>{{ item.alerts.high }} alta</span
								>
								<span
									v-if="item.alerts.medium"
									class="text-[11px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-600"
									>{{ item.alerts.medium }} media</span
								>
								<span
									v-if="item.alerts.low"
									class="text-[11px] font-semibold px-2 py-0.5 rounded bg-sky-100 text-sky-600"
									>{{ item.alerts.low }} baja</span
								>
								<span
									v-if="item.recommendationsCount"
									class="text-xs text-gray-400"
									>· {{ item.recommendationsCount }} recom.</span
								>
							</div>
						</button>

						<!-- Cargando siguiente página -->
						<div
							v-if="isFetchingNextPage"
							class="px-4 py-3 space-y-2 animate-pulse"
						>
							<div class="h-14 rounded-lg bg-gray-100" />
						</div>
					</div>
				</div>
			</div>
		</div>
		</template>
	</div>
</template>

<style>
	.ai-report-content {
		font-size: 0.875rem;
		line-height: 1.75;
		color: #374151;
	}

	.ai-report-content h2 {
		font-size: 1rem;
		font-weight: 700;
		color: #111827;
		margin-top: 1.5rem;
		margin-bottom: 0.5rem;
		padding-bottom: 0.375rem;
		border-bottom: 1px solid #f3f4f6;
	}

	.ai-report-content h2:first-child {
		margin-top: 0;
	}

	.ai-report-content h3 {
		font-size: 0.9375rem;
		font-weight: 600;
		color: #1f2937;
		margin-top: 1.25rem;
		margin-bottom: 0.375rem;
	}

	.ai-report-content p {
		margin-bottom: 0.75rem;
	}

	.ai-report-content ul {
		list-style: none;
		padding: 0;
		margin: 0 0 0.75rem 0;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	.ai-report-content ul li {
		padding-left: 1.25rem;
		position: relative;
	}

	.ai-report-content ul li::before {
		content: '•';
		position: absolute;
		left: 0;
		color: #9ca3af;
		font-weight: 700;
	}

	.ai-report-content ol {
		padding-left: 1.5rem;
		margin-bottom: 0.75rem;
		display: flex;
		flex-direction: column;
		gap: 0.375rem;
	}

	.ai-report-content strong {
		font-weight: 600;
		color: #111827;
	}

	.ai-report-content em {
		font-style: italic;
		color: #6b7280;
	}

	.ai-report-content ul li:has(.severity-badge) {
		padding-left: 0 !important;
	}

	.ai-report-content ul li:has(.severity-badge)::before {
		content: none !important;
		display: none !important;
	}

	.ai-report-content .severity-badge {
		display: inline-flex;
		align-items: center;
		padding: 0.2rem 0.625rem;
		border-radius: 9999px;
		font-size: 0.6875rem;
		font-weight: 600;
		margin-right: 0.375rem;
		vertical-align: middle;
		white-space: nowrap;
		border: 1px solid transparent;
	}

	.ai-report-content .severity-high {
		background: #fee2e2;
		color: #b91c1c;
		border-color: #fecaca;
	}

	.ai-report-content .severity-medium {
		background: #fef3c7;
		color: #b45309;
		border-color: #fde68a;
	}

	.ai-report-content .severity-low {
		background: #dbeafe;
		color: #1d4ed8;
		border-color: #bfdbfe;
	}
</style>
